import { CoverageIdEnum, PolicyProductIdEnum, PurchasedProductInterface, RelationInterface } from 'outshared-lib';
import { coverageBicycleData, coverageCarData } from '../data';
import { CoverageGroupTypeEnum } from '../enums';
import { CoverageControlConfigGroupInterface, CoverageItemInterface2, CoverageTableConfigInterface } from '../interfaces';
import { AllowedAgeUtility, MostPopularUtility } from '../utilities';
import { CoverageGroupAbstract } from './coverage-group.abstract';
import { CoverageInput } from './coverage-input.class';

export class CoverageGroupTable extends CoverageGroupAbstract {

    public coverages: CoverageTableConfigInterface;
    public mostPopular: CoverageIdEnum;

    constructor(
        group: CoverageControlConfigGroupInterface,
        purchasedProduct: PurchasedProductInterface,
        relation: RelationInterface,
    ) {
        super(CoverageGroupTypeEnum.Table, group, purchasedProduct);

        this.coverages = this.getCoverages(purchasedProduct);
        this.mostPopular = this.getMostPopular(purchasedProduct);

        // Validate if user is old enough for a coverage
        this.validateAllowedAgeForCoverageInput(relation.birthdate);
    }

    public getCoverageInput(coverageId: CoverageIdEnum): CoverageInput | null {
        const foundInput = this.inputs
            .find((input) => input.id === coverageId);

        if (!foundInput) {
            return null;
        }

        return foundInput;
    }

    public getCoverageItem(id: string): CoverageItemInterface2 | null {
        const coverageItem = this.coverages.items
            .find((item) => item.id === id);

        if (!coverageItem) {
            return null;
        }

        return coverageItem;
    }

    public findSelectedCoverageIds(coverageId: CoverageIdEnum): CoverageIdEnum[] {
        const foundInput = this.inputs
            .find((input) => input.id === coverageId);

        if (!foundInput) {
            return [];
        }

        return foundInput.coverageIds;
    }

    public findSelectedCoverageId(coverageIds: CoverageIdEnum[]): CoverageIdEnum | null {
        const foundInput = this.inputs
            .find((input) => JSON.stringify(input.coverageIds) === JSON.stringify(coverageIds));

        if (!foundInput) {
            return null;
        }

        return foundInput.id;
    }

    private getCoverages(purchasedProduct: PurchasedProductInterface): CoverageTableConfigInterface {
        const coverageData = this.getCoverageData(purchasedProduct.productId);
        const items = coverageData.items.map((item) => ({
            ...item,
            price: this.getInputPrice(item.id),
            disabled: this.getInputDisabled(item.id),
        }));

        return {
            descriptions: coverageData.descriptions,
            items: items,
            rows: coverageData.rows,
        };
    }

    private getCoverageData(productId: PolicyProductIdEnum): CoverageTableConfigInterface {
        switch (productId) {
            case PolicyProductIdEnum.Bicycle:
                return coverageBicycleData;
            default:
                return coverageCarData;
        }
    }

    // Type needs to support deprecated ABCD string codes
    private getInputPrice(coverageId: CoverageIdEnum | string): number {
        const foundInput = this.inputs
            .find((input) => input.id === coverageId);

        if (!foundInput) {
            return 0;
        }

        return foundInput.price;
    }

    // Type needs to support deprecated ABCD string codes
    private getInputDisabled(coverageId: CoverageIdEnum | string): boolean {
        const foundInput = this.inputs
            .find((input) => input.id === coverageId);

        if (!foundInput) {
            return false;
        }

        // If it's not accepted, it's disabled
        return !foundInput.acceptance;
    }

    private getMostPopular(purchasedProduct: PurchasedProductInterface): CoverageIdEnum | null {
        const mostPopularUtility = new MostPopularUtility();
        const mostPopularInput = this.inputs
            .find((input) => mostPopularUtility.isMostPopularCar(purchasedProduct, input));

        if (!mostPopularInput) {
            return null;
        }

        return mostPopularInput.id;
    }

    private validateAllowedAgeForCoverageInput(birthdate: string): void {
        const allowedAgeUtility = new AllowedAgeUtility();
        const isNotAllowed = allowedAgeUtility.isNotAllowed(birthdate);
        const coverageInput = this.inputs.find((input) => input.id === CoverageIdEnum.CarLegalLiability);

        if (isNotAllowed && coverageInput) {
            coverageInput.doNotAccept();
        }
    }
}
